"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Target, Zap, CircleDot, Activity, BookOpen, Telescope, RotateCcw } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

export default function CosmobiologyPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="flex h-screen">
        {/* Sidebar */}
        <AboutSidebar activeSection="astrology" />

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-red-300">
                <Telescope className="h-4 w-4" />
                <span className="text-sm font-medium">🔭 Astroloji</span>
                <span className="text-red-500">/</span>
                <span className="text-sm">Gelişmiş Sistemler</span>
                <span className="text-red-500">/</span>
                <span className="text-sm">Kozmobiyoloji</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Astroloji
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology/advanced-systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Gelişmiş Sistemler
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Kozmobiyoloji</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 rounded-xl p-8 mb-8 border border-red-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Target className="h-10 w-10 text-red-400" />
                Kozmobiyoloji (Cosmobiology)
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                Reinhold Ebertin tarafından geliştirilen, Hamburg Okulu'nun midpoint tekniklerini
                sadeleştirerek modern psikolojik astroloji ile birleştiren sistem.
                90° dial kullanımı, kombinasyon yorumları ve praktik zamanlama teknikleri
                ile astrolojik analizi daha sistematik hale getirir.
              </p>
            </div>

            {/* Basic Concept */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Temel Kavramlar</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <RotateCcw className="h-5 w-5 text-red-400" />
                      90° Dial Sistemi
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        360°'lik zodiakın 90°'lik dört parçaya bölünmesi ile
                        tüm hard aspectler aynı eksende görünür hale gelir.
                      </p>
                      <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-red-200 mb-2">Eşdeğer Pozisyonlar:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>0° Koç = 0° Yengeç = 0° Terazi = 0° Oğlak</div>
                          <div>15° Boğa = 15° Aslan = 15° Akrep = 15° Kova</div>
                          <div>Sadece conjunction (0°) aspecti kullanılır</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <CircleDot className="h-5 w-5 text-orange-400" />
                      Kombinasyon Prensibi
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        İki veya daha fazla gezegen faktörünün birleşimi
                        yeni anlamlar ve potansiyeller yaratır.
                      </p>
                      <div className="bg-orange-900/20 border border-orange-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-orange-200 mb-2">Örnek Kombinasyon:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>Güneş/Mars = Enerji, liderlik, cesaret</div>
                          <div>Ay/Venüs = Duygusal uyum, estetik, cazibe</div>
                          <div>Merkür/Jüpiter = Öğretim, yayıncılık, bilgelik</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Reinhold Ebertin */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Reinhold Ebertin ve Tarihsel Gelişim</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-amber-300">Reinhold Ebertin (1901-1988)</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Alman astrolog ve yayıncı. Hamburg Okulu'nun karmaşık sistemini
                      sadeleştirerek modern kozmobiyolojiyi kurdu.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>"Combination of Stellar Influences" (1940):</strong> Ana eseri</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Kosmobiologie dergisi:</strong> 50 yıl yayın</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Hamburg Okulu'ndan ayrılma:</strong> Daha basit sistem</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-green-300">Modern Gelişim</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Alfred Witte'nin Hamburg Okulu sistemini basitleştirerek
                      pratik uygulamaya odaklandı.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Transneptün gezegenleri:</strong> Kaldırıldı</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Psikolojik astroloji:</strong> Modern yaklaşım</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Bilimsel araştırma:</strong> İstatistiksel çalışmalar</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Major Combinations */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Ana Kombinasyonlar</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Sun Combinations */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☉</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Güneş Kombinasyonları</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☉/☽ (Güneş/Ay)</div>
                      <div className="text-sm text-slate-400">Yaşam gücü, vitality, enerji dengesi</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☉/♂ (Güneş/Mars)</div>
                      <div className="text-sm text-slate-400">Liderlik, cesaret, mücadeleci ruh</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☉/♃ (Güneş/Jüpiter)</div>
                      <div className="text-sm text-slate-400">Başarı, genişleme, iyimserlik</div>
                    </div>
                  </div>
                </div>

                {/* Moon Combinations */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☽</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Ay Kombinasyonları</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☽/♀ (Ay/Venüs)</div>
                      <div className="text-sm text-slate-400">Duygusal uyum, güzellik, sanat</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☽/♆ (Ay/Neptün)</div>
                      <div className="text-sm text-slate-400">Sezgi, empati, ruhsal duyarlılık</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☽/♇ (Ay/Pluto)</div>
                      <div className="text-sm text-slate-400">Derin duygular, transformasyon</div>
                    </div>
                  </div>
                </div>

                {/* Mercury Combinations */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☿</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Merkür Kombinasyonları</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☿/♃ (Merkür/Jüpiter)</div>
                      <div className="text-sm text-slate-400">Öğretmenlik, yayıncılık, bilgelik</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☿/♄ (Merkür/Satürn)</div>
                      <div className="text-sm text-slate-400">Sistematik düşünce, disiplin</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☿/♅ (Merkür/Uranüs)</div>
                      <div className="text-sm text-slate-400">Yaratıcı zeka, teknoloji</div>
                    </div>
                  </div>
                </div>

                {/* Venus Combinations */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♀</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Venüs Kombinasyonları</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♀/♂ (Venüs/Mars)</div>
                      <div className="text-sm text-slate-400">Aşk, tutku, cinsel çekim</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♀/♃ (Venüs/Jüpiter)</div>
                      <div className="text-sm text-slate-400">Sosyal başarı, bolluk, şans</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♀/♄ (Venüs/Satürn)</div>
                      <div className="text-sm text-slate-400">Sadakat, kalıcı ilişkiler</div>
                    </div>
                  </div>
                </div>

                {/* Mars Combinations */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♂</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Mars Kombinasyonları</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♂/♃ (Mars/Jüpiter)</div>
                      <div className="text-sm text-slate-400">Büyük projeler, adalet mücadelesi</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♂/♄ (Mars/Satürn)</div>
                      <div className="text-sm text-slate-400">Kontrollü güç, disiplinli eylem</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♂/♇ (Mars/Pluto)</div>
                      <div className="text-sm text-slate-400">Yoğun güç, radikal değişim</div>
                    </div>
                  </div>
                </div>

                {/* Outer Planet Combinations */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">⚪</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Dış Gezegen Kombinasyonları</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♅/♆ (Uranüs/Neptün)</div>
                      <div className="text-sm text-slate-400">Yaratıcı vizyon, mistik yenilik</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♅/♇ (Uranüs/Pluto)</div>
                      <div className="text-sm text-slate-400">Devrimci değişim, teknolojik atılım</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♆/♇ (Neptün/Pluto)</div>
                      <div className="text-sm text-slate-400">Ruhsal transformasyon, collective değişim</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Practical Application */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Pratik Uygulama</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Activity className="h-5 w-5 text-cyan-400" />
                    90° Dial Okuma
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Tüm gezegen pozisyonları 90°'lik dilim içinde gösterilir.
                      Aynı eksende olan faktörler kombine edilir.
                    </p>
                    <div className="bg-cyan-900/20 border border-cyan-700/50 rounded-lg p-4">
                      <h4 className="font-semibold text-cyan-200 mb-2">Analiz Adımları:</h4>
                      <div className="space-y-1 text-sm text-slate-300">
                        <div>1. Tüm pozisyonları 90° dial'a yerleştir</div>
                        <div>2. 0°30' orb içindeki faktörleri grupla</div>
                        <div>3. 2'li, 3'lü ve 4'lü kombinasyonları belirle</div>
                        <div>4. Her kombinasyonun anlamını yorumla</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Zap className="h-5 w-5 text-orange-400" />
                    Transit Aktivasyonu
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Transit gezegenler natal kombinasyonları tetikler.
                      Özellikle slow-moving gezegenler önemlidir.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Solar Arc aktivasyonu:</strong> En güvenilir yöntem</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Transit aktivasyonu:</strong> Anlık tetikleme</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Progressed aktivasyon:</strong> İç gelişim</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Studies */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Örnek Vaka Analizleri</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-emerald-300">☉/♂/♃ Kombinasyonu (15° Koç)</h3>
                  <div className="space-y-3">
                    <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-lg p-4">
                      <div className="font-medium text-emerald-200 mb-2">Natal Potansiyel:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Doğal liderlik yetenekleri</div>
                        <div>• Büyük proje yönetme becerisi</div>
                        <div>• Enerji ve optimizmin birleşimi</div>
                        <div>• Spor veya rekabet alanlarında başarı</div>
                      </div>
                    </div>
                    <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
                      <div className="font-medium text-blue-200 mb-2">Aktivasyon (SA ♄ = 15° Koç):</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Büyük sorumluluk alma dönemi</div>
                        <div>• Kariyerde kritik karar zamanı</div>
                        <div>• Liderlik testleri ve zorluklar</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-rose-300">☽/♀/♆ Kombinasyonu (22° Balık)</h3>
                  <div className="space-y-3">
                    <div className="bg-rose-900/20 border border-rose-700/30 rounded-lg p-4">
                      <div className="font-medium text-rose-200 mb-2">Natal Özellikler:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Yüksek estetik duyarlılık</div>
                        <div>• Empati ve şefkat yeteneği</div>
                        <div>• Sanat ve müzik yetisi</div>
                        <div>• Duygusal sezgi gücü</div>
                      </div>
                    </div>
                    <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-4">
                      <div className="font-medium text-purple-200 mb-2">Transit ♃ Aktivasyonu:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Sanatsal projelerde büyük fırsat</div>
                        <div>• Duygusal bolluk ve tatmin dönemi</div>
                        <div>• Ruhsal gelişimde genişleme</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Techniques */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">İleri Teknikler</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 text-indigo-300">Midpoint Trees</h3>
                    <div className="space-y-3">
                      <p className="text-slate-300 text-sm">
                        Bir kombinasyonun detaylı analizi için ağaç diyagramı kullanımı.
                        Her seviyede daha spesifik yorumlar geliştirmeye olanak sağlar.
                      </p>
                      <div className="bg-indigo-900/20 border border-indigo-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-indigo-200 mb-2">Örnek Tree (☉/♂):</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>Level 1: Güneş + Mars = Liderlik</div>
                          <div>Level 2: + Venüs = Sanatsal liderlik</div>
                          <div>Level 3: + MC = Kamusal tanınma</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 text-teal-300">Rhythmic Activation</h3>
                    <div className="space-y-3">
                      <p className="text-slate-300 text-sm">
                        7 yıllık ritmik döngülerle kombinasyonların aktivasyon zamanlarını
                        belirleyen Ebertin'in özel tekniği.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-teal-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">7-14-21-28... yaş dönemleri</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-teal-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Her dönemde farklı kombinasyon aktif</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-2 h-2 bg-teal-400 rounded-full mt-1.5 flex-shrink-0"></div>
                          <span className="text-sm text-slate-400">Yaşam planlaması için kullanılır</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Study Resources */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Önerilen Kaynaklar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Temel Eserler</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "The Combination of Stellar Influences" - Reinhold Ebertin</div>
                    <div>• "Applied Cosmobiology" - Reinhold Ebertin</div>
                    <div>• "Transits" - Reinhold Ebertin</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Modern Kaynaklar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Cosmobiology Handbook" - Udo Rudolph</div>
                    <div>• "Midpoint Analysis" - Mary Fortier Shea</div>
                    <div>• "Working with Astrology" - Michael Harding & Charles Harvey</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-200 mb-3">⚠️ Önemli Açıklama</h3>
              <p className="text-red-100 leading-relaxed">
                Kozmobiyoloji sistematik analiz gerektiren ileri düzey bir tekniktir.
                Bu bilgiler sadece eğitim amaçlıdır ve hiçbiri bilimsel kesinlik iddiası taşımaz.
                Profesyonel kozmobiyoloji analizi için deneyimli uzmanlarla çalışılması önerilir.
              </p>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/astrology/advanced-systems/statistical"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                İstatistiksel Astroloji
              </Link>

              <Link
                href="/about/astrology/advanced-systems/astrocartography"
                className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                Astrokartografya
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}